import { MutableRefObject, useEffect, useRef, useState } from "react"
import { ToggleSlider } from "react-toggle-slider"
import './HexBuilder.scss'
import colors from './HexBuilder.module.scss';
import { produce } from "immer"
import { IntervalLines } from "../../../Comps/Connectors/IntervalLines";
import { HxMapGroup } from "../../../Comps/HxMap/HxMapGroup";
import { NoteButtons } from "../../../Comps/NoteButtons/NoteButtons";
import { HexMap } from "../../../Data/HexMap/HexMap";
import { HexMapBuilderParams, BuildHexMap } from "../../../Data/HexMap/HexMapBuilder";
import { HexMapModifier } from "../../../Data/HexMap/HexMapModifier";
import { Note } from "../../../Data/HexMap/Note";
import { All } from "../../../Util/Constants/Quantities";
import { Music } from "../../../Util/Music/Music";
import { TransformTranslate } from "../../../Util/Transform/Transform";
import { NotesSelect } from "../../../Comps/NoteButtons/NotesSelect";
import { ExerciseMessage } from "../Utils/Message/ExerciseMessage";
import { OutlineBox } from "../../../Comps/OutlineBox/OutlineBox";
import { useNoteNames } from "../../../Project/ProjectStore";
import { useNotePlayer } from "../../../Sound/SoundStore";
import { NotePlayer } from "../../../Sound/NotePlayer";

export function HexBuilder(){
    const autoAdvance:MutableRefObject<boolean> = useRef(true)
    const noteNames:string[] = useNoteNames()
    const tonic:MutableRefObject<number> = useRef(Music.G)
    const notePlayer:MutableRefObject<NotePlayer> = useRef(useNotePlayer())
    useEffect(()=>{notePlayer.current.setTonic(tonic.current)},[])
    const getInitialMessage = () => {
        return `Building the key of ${noteNames[tonic.current]}. Indentify the next note.`
    }
    const [message, setMessage] = useState(getInitialMessage())
    const answerCorrect:MutableRefObject<boolean|null> = useRef(null)
    const complete:MutableRefObject<boolean> = useRef(false)
    const [showIntervals, setShowIntervals] = useState<boolean>(false)
    const selectedNote:MutableRefObject<number> = useRef(1)
    const hexRadius:number = 100
    const hexHeight:number = hexRadius * 2.8
    const hexWidth:number = hexRadius * 2.8
    const initializeMap = (modifier:HexMapModifier) => {
        modifier.notes_setPrimaryLabel([All], ' ')
        modifier.notes_setSelectedState([All], Note.SelectedState_Default)
        modifier.notes_setPrimaryLabel([0], null)
        modifier.notes_setSelectedState([0], Note.SelectedState_Selected)
        if(autoAdvance.current){
            modifier.notes_setSelectedState([selectedNote.current], Note.SelectedState_Hilited)
        }     
    }
    const onNoteClick = (note:Note) => { // handler - converted
        if(note.params.noteIndex === selectedNote.current){
             setMap(produce((draft:HexMap)=>{
                mod.current.setDraft(draft)
                mod.current.notes_setSelectedState([selectedNote.current], Note.SelectedState_Hilited)
            }))
            setMessage("That's the right position. Next step: indentify the note.")
        }
    }
    const onKeyClick = ()=>{}
    const getHexMap = (i:number = 0) => {
        const params:HexMapBuilderParams = {
            rows:1, columns:1, tonic:tonic.current, radius:hexRadius
        }
        const map:HexMap = BuildHexMap(params)
        const mod:HexMapModifier = new HexMapModifier(map)
        initializeMap(mod)
        return map
    }
    const [map, setMap] = useState(getHexMap())
    const mod:MutableRefObject<HexMapModifier> = useRef(new HexMapModifier(map))
 
    const onComplete = () => {
        setMessage('Correct! The key is completed. Select a new key or hit reset to go again.')
        answerCorrect.current = true
        selectedNote.current = 0
        notePlayer.current.reset()
    }
    // #handler
    const onNoteButtonClick = (noteTonic:number) => {
        const currentTonic:number = Music.getKeyScale(tonic.current)[selectedNote.current]
        const nextIndex:number = selectedNote.current + 1
        const isLastNote:boolean = (selectedNote.current === 6)
        const selectedNoteIndex:number = selectedNote.current
        if(currentTonic === noteTonic){
            notePlayer.current.playNote(noteTonic)
            answerCorrect.current = true
            setMap(produce((draft:HexMap)=>{
                    mod.current.setDraft(draft)
                    mod.current.notes_setPrimaryLabel([selectedNoteIndex], null)
                    mod.current.notes_setSelectedState([All], Note.SelectedState_Default)
                    draft.notes.forEach((note:Note)=>{
                        if(note.primaryLabel === null){note.selectedState = Note.SelectedState_Selected}
                    })
                    if(autoAdvance.current && !isLastNote){
                        mod.current.notes_setSelectedState([nextIndex], Note.SelectedState_Hilited)
                    }
            }))
            if(isLastNote){    
                onComplete()
            }
            else{
                selectedNote.current = selectedNote.current + 1
                setMessage('Correct! Next step: indentify the next note.')
            }
        }
        else{
            notePlayer.current.playBuzzer()
            answerCorrect.current = false 
            setMessage('Incorrect. Indentify the current note.')
        }
    }
    // handler
    const resetExercise = () => {  
        selectedNote.current = 1
        answerCorrect.current = null
        setMap(getHexMap())
        setMessage(getInitialMessage())
        notePlayer.current.setTonic(tonic.current)
    }
    // #handler
    const changeTonic = (freshTonic:number) => {
        if(tonic.current !== freshTonic){
            notePlayer.current.setTonic(freshTonic)
            tonic.current = freshTonic
            answerCorrect.current = null
            selectedNote.current = 1
            setMessage(getInitialMessage())
            setMap(produce((draft:HexMap)=>{
                mod.current.setDraft(draft)
                mod.current.setTonic(tonic.current)
                mod.current.notes_setSelectedState([All], Note.SelectedState_Default)
                if(autoAdvance.current){
                    mod.current.notes_setSelectedState([1], Note.SelectedState_Hilited)
                }
                mod.current.notes_setPrimaryLabel([All], ' ')
                mod.current.notes_setPrimaryLabel([0], null)
                mod.current.notes_setSelected([0])
            }))
        }
    }
    // #handler
    const toggleIntervals = () => {
        setShowIntervals(!showIntervals)
    }
    // #handler
    const toggleAutoAdvance = () => {
        //updateMod()
        autoAdvance.current = !autoAdvance.current
        const selectedNoteIndex:number = selectedNote.current
        if(autoAdvance.current){
            setMap(produce((draft:HexMap)=>{
                mod.current.setDraft(draft)
                mod.current.notes_setSelectedState([selectedNoteIndex], Note.SelectedState_Hilited)
            }))
        }
        else{
            setMap(produce((draft:HexMap)=>{
                mod.current.setDraft(draft)      
                mod.current.draft.notes.forEach((note:Note)=>{
                    if(note.primaryLabel === ' '){
                        mod.current.notes_setSelectedState([note.params.noteIndex], Note.SelectedState_Default)
                    }
                })     
            }))
        }
    }
    const intervalLines:MutableRefObject<JSX.Element> = useRef(<IntervalLines notes={map.notes} displayConfig={map.params.displayConfig}/>)
    return(
        <OutlineBox title={'Hex Key Builder Exercise'} width={800}>
            <div className={'hex-builder'}>
                <div><ExerciseMessage forceRefresh={true} message={message} answerStatus={answerCorrect.current}/></div>
                <svg width={hexWidth} height={hexHeight}>
                    <g transform={TransformTranslate(hexWidth/2,hexHeight/2)}>
                        <HxMapGroup hexMap={map} onNoteClick={onNoteClick} onKeyClick={onKeyClick}>
                            {showIntervals && intervalLines.current}
                        </HxMapGroup>
                    </g>
                </svg><div>
                    <NoteButtons alphaOrder={true} key={'singleton'} onClick={onNoteButtonClick}/>
                </div>
                <div className={'hex-builder-settings'}>
                    <div style={{display:'inline-block', marginRight:'20px'}}>
                        <div style={{display:"flex"}}>
                            <NotesSelect tonic={tonic.current} onChange={changeTonic}></NotesSelect>
                        </div>
                    </div>
                    <div style={{display:'inline-block', marginRight:'20px'}}>
                        <div style={{display:"flex"}}>
                            <div style={{display:'flex', marginRight:'10px'}}>Auto Advance: </div>
                            <ToggleSlider barBackgroundColorActive={colors.sliderColor} onToggle={toggleAutoAdvance} active={autoAdvance.current}/>
                        </div>
                    </div>
                    <div style={{display:'inline-block', marginRight:'20px'}}>
                        <div style={{display:"flex"}}>
                            <button type='button' onClick={resetExercise}>reset</button>
                        </div>
                    </div>
                </div>
                <div></div>
            </div>
        </OutlineBox>

        
    )
}